import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Eye, EyeOff, ChevronDown, ChevronRight, FileText, User } from 'lucide-react';
import { FIELD_SECTIONS, formatValue } from '../applications/loanFieldConstants';

const DATA_KEY_MAP = {
  sba_loan: 'sba_loan_data',
  term_loan: 'term_loan_data',
  term_loan_10yr: 'term_loan_10yr_data',
  term_loan_3_5yr: 'term_loan_3_5yr_data',
  term_loc_hybrid: 'term_loc_hybrid_data',
  line_of_credit: 'line_of_credit_data',
  equipment_financing: 'equipment_financing_data',
  merchant_processing: 'merchant_processing_data',
  jumbo_term_loan: 'jumbo_term_loan_data',
  transportation_factoring: 'transportation_factoring_data',
  real_estate_financing: 'real_estate_financing_data',
  debt_consulting: 'debt_consulting_data',
};

const SENSITIVE_FIELDS = ['social_security_number', 'ssn_itin', 'owner_ssn', 'owner1_ssn', 'ssn', 'borrower_ssn', 'primary_contact_ssn'];

const maskSSN = (value) => {
  const str = String(value);
  if (str.length <= 4) return '***-**-' + str;
  return '***-**-' + str.slice(-4);
};

const SensitiveField = ({ value, testId }) => {
  const [visible, setVisible] = useState(false);
  if (!value) return null;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span>{visible ? String(value) : maskSSN(value)}</span>
      <button
        onClick={() => setVisible(!visible)}
        className="text-slate-400 hover:text-slate-600 transition-colors p-0.5 rounded"
        title={visible ? 'Hide SSN' : 'Show SSN'}
        data-testid={testId || 'fields-panel-ssn-toggle'}
      >
        {visible ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
      </button>
    </span>
  );
};

const isFilled = (v) => {
  if (v === null || v === undefined || v === '') return false;
  if (Array.isArray(v) && v.length === 0) return false;
  return true;
};

const renderValue = (key, value, fmt) => {
  if (!isFilled(value)) return null;
  if (Array.isArray(value)) {
    if (value.every(v => typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'))
      return value.join(', ');
    return null;
  }
  if (typeof value === 'object') return null;
  if (SENSITIVE_FIELDS.includes(key)) return <SensitiveField value={value} />;
  const formatted = formatValue(value, fmt);
  return formatted ?? String(value);
};

const ApplicationFieldsPanel = ({ application, defaultExpanded = false }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [hideEmpty, setHideEmpty] = useState(false);

  const productType = application?.product_type;
  const dataKey = DATA_KEY_MAP[productType] || (productType ? `${productType}_data` : '');
  const loanData = application?.[dataKey] || {};
  const sections = FIELD_SECTIONS[productType] || [];

  const { totalFields, filledFields } = useMemo(() => {
    let total = 0;
    let filled = 0;
    sections.forEach(section => {
      section.fields.forEach(field => {
        total += 1;
        if (isFilled(loanData[field.key])) filled += 1;
      });
    });
    return { totalFields: total, filledFields: filled };
  }, [sections, loanData]);

  const additionalOwners = loanData.additional_owners || loanData.officers || [];

  if (!productType || sections.length === 0) return null;

  const completionPct = totalFields > 0 ? Math.round((filledFields / totalFields) * 100) : 0;
  const completionColor = completionPct >= 90
    ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
    : completionPct >= 60
      ? 'bg-amber-100 text-amber-700 border-amber-200'
      : 'bg-rose-100 text-rose-700 border-rose-200';

  return (
    <Card data-testid="application-fields-panel" className="lg:col-span-3">
      <CardHeader className="cursor-pointer select-none pb-3" onClick={() => setExpanded(prev => !prev)}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-base flex items-center gap-2">
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            <FileText className="h-5 w-5" />
            All Application Fields
            <span
              className={`ml-2 text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border ${completionColor}`}
              data-testid="fields-panel-completion-pill"
            >
              {filledFields}/{totalFields} filled · {completionPct}%
            </span>
          </CardTitle>
          {expanded && (
            <button
              onClick={(e) => { e.stopPropagation(); setHideEmpty(prev => !prev); }}
              className="text-xs px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 font-medium"
              data-testid="fields-panel-toggle-empty"
            >
              {hideEmpty ? 'Show empty fields' : 'Hide empty fields'}
            </button>
          )}
        </div>
        {!expanded && (
          <p className="text-xs text-slate-500 mt-1 ml-6">
            Click to view every field captured for this application — including which fields are still empty.
          </p>
        )}
      </CardHeader>
      {expanded && (
        <CardContent className="space-y-6 pt-0">
          {sections.map((section, sIdx) => {
            const fieldsToRender = hideEmpty
              ? section.fields.filter(f => isFilled(loanData[f.key]))
              : section.fields;
            if (fieldsToRender.length === 0) return null;
            const sectionFilled = section.fields.filter(f => isFilled(loanData[f.key])).length;
            return (
              <div key={sIdx} data-testid={`fields-panel-section-${sIdx}`}>
                <div className="flex items-center justify-between mb-3 pb-1 border-b border-slate-100">
                  <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider">
                    {section.title}
                  </h4>
                  <span className="text-[10px] text-slate-400 font-medium">
                    {sectionFilled}/{section.fields.length}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
                  {fieldsToRender.map(field => {
                    const v = loanData[field.key];
                    const rendered = renderValue(field.key, v, field.fmt);
                    const hasValue = rendered !== null && rendered !== undefined && rendered !== '';
                    return (
                      <div key={field.key} className="py-1" data-testid={`fields-panel-field-${field.key}`}>
                        <dt className="text-xs text-slate-500 font-medium uppercase tracking-wide">
                          {field.label}
                        </dt>
                        <dd
                          className={`text-sm font-medium mt-0.5 break-words ${hasValue ? 'text-slate-800' : 'text-slate-300 italic'}`}
                          data-testid={`fields-panel-value-${field.key}`}
                        >
                          {hasValue ? rendered : 'Not provided'}
                        </dd>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Additional owners / officers */}
          {Array.isArray(additionalOwners) && additionalOwners.length > 0 && (
            <div data-testid="fields-panel-additional-owners">
              <div className="flex items-center justify-between mb-3 pb-1 border-b border-slate-100">
                <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider flex items-center gap-1.5">
                  <User className="h-3.5 w-3.5" />
                  Additional Owners / Officers ({additionalOwners.length})
                </h4>
              </div>
              <div className="space-y-3">
                {additionalOwners.map((owner, idx) => {
                  const entries = Object.entries(owner).filter(([k, v]) => isFilled(v) && typeof v !== 'object');
                  if (entries.length === 0) return null;
                  return (
                    <div key={idx} className="p-3 bg-slate-50 border border-slate-200 rounded-lg" data-testid={`fields-panel-owner-${idx}`}>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 text-sm">
                        {entries.map(([k, v]) => (
                          <div key={k}>
                            <dt className="text-xs text-slate-500 font-medium uppercase tracking-wide">
                              {k.replace(/_/g, ' ')}
                            </dt>
                            <dd className="font-medium text-slate-800 break-words">
                              {SENSITIVE_FIELDS.includes(k) ? <SensitiveField value={v} testId={`fields-panel-owner-${idx}-ssn-toggle`} /> : String(v)}
                            </dd>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default ApplicationFieldsPanel;
